/**
 * Main App Component
 */
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { useAppSelector } from './store/hooks';
import { checkIsAdmin, initializeAuth, logout } from './store/authSlice';
import { useEffect, useRef } from 'react';
import { useSocketStore } from './store/useSocketStore';
import { authAPI } from './api/authAPI';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from './utils/constants';
import { PARENT_APP_ALLOWED_ORIGINS } from './utils/parentAppOrigins';
import { isTokenExpiringSoon, getTimeUntilExpiry } from './utils/jwt';

// Components
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';

// Pages
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { ForYou } from './pages/ForYou';
import { Projects } from './pages/Projects';
import { Tasks } from './pages/Tasks';
import { Kanban } from './pages/Kanban';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { Summary } from './pages/Summary';
import { ProjectList } from './pages/ProjectList';
import { ProjectMembersPage } from './pages/ProjectMembersPage';
import { Teams } from './pages/Teams';
import { TeamDetails } from './pages/TeamDetails';
import { Calendar } from './pages/Calendar';
import { Sprints } from './pages/Sprints';
import Timeline from './pages/Timeline';
import { OnboardUser } from './pages/OnboardUser';
import { CreateTeam } from './pages/CreateTeam';
import { TaskView } from './pages/TaskView';
import { SubtaskView } from './pages/SubtaskView';
import { BASE_DOCUMENT_TITLE } from './utils/documentTitle';
import { OtpVerification } from './pages/OtpVerification';
import { EmbedAuth } from './pages/EmbedAuth.tsx';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const formatTitleSegment = (segment: string) =>
  segment
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const projectSectionTitles: Record<string, string> = {
  summary: 'Summary',
  timeline: 'Timeline',
  calendar: 'Calendar',
  backlog: 'Backlog',
  board: 'Board',
  reports: 'Reports',
  list: 'List',
  forms: 'Forms',
  goals: 'Goals',
  development: 'Development',
  code: 'Code',
  archived: 'Archived',
  pages: 'Pages',
  people: 'People',
  sprints: 'Sprints',
  kanban: 'Kanban',
};

const staticRouteTitles: Record<string, string> = {
  '/': 'Welcome',
  '/for-you': 'For You',
  '/dashboard': 'Dashboard',
  '/projects': 'Projects',
  '/teams': 'Teams',
  '/tasks': 'Tasks',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/login': 'Login',
  '/register': 'Register',
  '/forgot-password': 'Forgot Password',
  '/forgetPassword': 'Reset Password',
  '/onboard-user': 'Onboard User',
  '/create-team': 'Create Team',
};

const buildDocumentTitle = (pathname: string, opts: { projectName?: string | null; projectKey?: string | null } = {}) => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  if (/^\/task\/[^/]+/.test(normalizedPath) || /^\/subtask\/[^/]+/.test(normalizedPath)) {
    // Shareable links handle their own titles (TaskView/SubtaskView)
    return null;
  }
  const staticTitle = staticRouteTitles[normalizedPath];

  if (staticTitle) {
    return `${staticTitle} | ${BASE_DOCUMENT_TITLE}`;
  }

  const taskMatch = normalizedPath.match(/^\/task\/([^/]+)/);
  if (taskMatch) {
    return `Task ${taskMatch[1]} | ${BASE_DOCUMENT_TITLE}`;
  }

  const subtaskMatch = normalizedPath.match(/^\/subtask\/([^/]+)/);
  if (subtaskMatch) {
    return `Subtask ${subtaskMatch[1]} | ${BASE_DOCUMENT_TITLE}`;
  }

  const projectsMatch = normalizedPath.match(/^\/projects\/([^/]+)(?:\/([^/]+))?/);
  if (projectsMatch) {
    const [, projectId, section] = projectsMatch;
    const fallbackProjectLabel = opts.projectName || opts.projectKey || `Project ${decodeURIComponent(projectId)}`;

    if (!section) {
      return `${fallbackProjectLabel} · Board | ${BASE_DOCUMENT_TITLE}`;
    }

    const sectionTitle = projectSectionTitles[section] || formatTitleSegment(section);
    return `${fallbackProjectLabel} · ${sectionTitle} | ${BASE_DOCUMENT_TITLE}`;
  }

  return BASE_DOCUMENT_TITLE;
};

const DocumentTitleManager = () => {
  const location = useLocation();
  const { currentProject } = useAppSelector((state) => state.projects);

  useEffect(() => {
    const nextTitle = buildDocumentTitle(location.pathname, {
      projectName: currentProject?.name,
      projectKey: currentProject?.key,
    });
    if (nextTitle) {
      document.title = nextTitle;
    }
  }, [location.pathname, currentProject?.name, currentProject?.key]);

  return null;
};

function AppRoutes() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { connect, disconnect, joinRoom } = useSocketStore();

  // Parent postMessage allowlist: edit array in `src/utils/parentAppOrigins.ts`
  useEffect(() => {
    const allowedOrigins = PARENT_APP_ALLOWED_ORIGINS;

    const isLogoutRequest = (payload: unknown): boolean => {
      if (typeof payload === 'object' && payload !== null) {
        const candidate = payload as Record<string, unknown>;
        const type = typeof candidate.type === 'string' ? candidate.type : '';
        return [
          'logout-request',
          'session-logout',
          'session-logged-out-request',
          'parent-logout',
          "logout"
        ].includes(type);
      }
      return payload === 'logout-request';
    };

    const onMessage = (event: MessageEvent) => {
      if (window.parent === window || event.source !== window.parent) return;
      if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
        return;
      }
      if (!isLogoutRequest(event.data)) return;
      dispatch(logout() as any);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [dispatch]);

  // Initialize authentication on app start
  useEffect(() => {
    dispatch(initializeAuth() as any);
  }, [dispatch]);

  // Check admin status when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(checkIsAdmin() as any);
    }
  }, [isAuthenticated, dispatch]);

  // Background token refresh timer - refresh token before it expires
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer if user is not authenticated
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    const scheduleTokenRefresh = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (!token || !refreshToken) {
        return;
      }

      // Check if token is expiring soon (within 5 minutes)
      if (isTokenExpiringSoon(token, 5)) {
        // Refresh immediately
        authAPI.refreshToken(refreshToken).then((res) => {
          localStorage.setItem(TOKEN_KEY, res.access_token);
          localStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token);
          // Reschedule for the new token
          scheduleTokenRefresh();
        }).catch((error) => {
          console.error('Background token refresh failed:', error);
          // Clear tokens on refresh failure - user will be logged out on next API call
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        });
      } else {
        // Calculate time until token expires (with 5 minute buffer)
        const timeUntilExpiry = getTimeUntilExpiry(token);
        if (timeUntilExpiry !== null) {
          const refreshTime = Math.max(0, timeUntilExpiry - (5 * 60 * 1000)); // Refresh 5 minutes before expiry
          
          // Clear existing timer
          if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
          }
          
          // Schedule refresh
          refreshTimerRef.current = setTimeout(() => {
            scheduleTokenRefresh();
          }, refreshTime);
        }
      }
    };

    // Schedule initial refresh check
    scheduleTokenRefresh();

    // Also set up a periodic check every 5 minutes as a backup
    const intervalId = setInterval(() => {
      scheduleTokenRefresh();
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  // Connect socket after auth and join org room (only if WebSocket is enabled)
  useEffect(() => {
    // Feature flag: only enable when explicitly set to 'true'
    const enableWebSocket = import.meta.env.VITE_ENABLE_WEBSOCKET === 'false';
    if (!enableWebSocket) {
      return; // Skip WebSocket connection if disabled
    }
    
    if (isAuthenticated) {
      try {
        connect();
        if (user && (user as any).organization_id) {
          // Only join room after a short delay to ensure socket is connected
          setTimeout(() => {
            joinRoom(`organization:${(user as any).organization_id}`);
          }, 500);
        }
      } catch (error) {
        console.warn('WebSocket connection failed, continuing without real-time updates:', error);
      }
    } else {
      disconnect();
    }
  }, [isAuthenticated, user, connect, disconnect, joinRoom]);

  // In App.tsx or main.tsx
  return (
    <Router>
      <DocumentTitleManager />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/forgetPassword"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route path="/embed" element={<EmbedAuth />} />

        {/* Protected Routes */}
        <Route
          path="/for-you"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ForYou />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Projects />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Teams />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams/:teamId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TeamDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/summary"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Summary />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/timeline"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Timeline />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/calendar"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Calendar />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/backlog"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/board"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/reports"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/list"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProjectList />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/forms"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/goals"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/development"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/code"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/archived"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/pages"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/people"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProjectMembersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/sprints"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Sprints />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/kanban"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Kanban />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Default project route - redirect to board */}
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Kanban />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Tasks />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Reports />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Settings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboard-user"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OnboardUser />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-team"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CreateTeam />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/task/:taskId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TaskView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subtask/:subtaskId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SubtaskView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite"
          element={
              <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 p-4">
                <OtpVerification />
              </div>
          }
        />

        {/* Default Route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </QueryClientProvider>
    </Provider>
  );
}

export default App;

